import React from "react";
import Page from "../components/Page";
import Box from "../components/patterns/Box";
import Flex from "../components/patterns/Flex";
import Text from "../components/patterns/Text";
import ButtonPill from "../components/patterns/ButtonPill";
import TabContainer, { TabProps } from "../components/patterns/Tabs";
import { isDesktop } from "react-device-detect";
import { db, analytics } from "../utils/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  startAfter,
  DocumentSnapshot,
  QueryConstraint,
} from "firebase/firestore";
import { DiscoverStoryCard } from "../components/discover/DiscoverStoryCard";
import { logEvent } from "firebase/analytics";

enum SortMode {
  trending = "trending",
  newest = "newest",
  mostViewed = "mostViewed",
}

const PAGE_SIZE = 12;

const Discover = () => {
  const [sortMode, setSortMode] = React.useState<SortMode>(SortMode.trending);
  const [stories, setStories] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [lastDoc, setLastDoc] = React.useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = React.useState(true);

  const fetchStories = async (isLoadMore = false) => {
    setLoading(true);

    const constraints: QueryConstraint[] = [
      where("published", "==", true),
    ];

    switch (sortMode) {
      case SortMode.trending:
        constraints.push(orderBy("totalLikes", "desc"));
        break;
      case SortMode.newest:
        constraints.push(orderBy("createdAt", "desc"));
        break;
      case SortMode.mostViewed:
        constraints.push(orderBy("views", "desc"));
        break;
    }

    constraints.push(limit(PAGE_SIZE));

    if (isLoadMore && lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    try {
      const q = query(collection(db, "stories"), ...constraints);
      const snapshot = await getDocs(q);

      const newStories = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (isLoadMore) {
        setStories((prev) => [...prev, ...newStories]);
      } else {
        setStories(newStories);
      }

      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error("Error fetching stories:", err);
    }

    setLoading(false);
  };

  React.useEffect(() => {
    setLastDoc(null);
    setHasMore(true);
    setStories([]);
    fetchStories(false);
    logEvent(analytics, "discover_page_view");
  }, [sortMode]);

  const tabs: TabProps[] = [
    {
      title: "Trending",
      id: SortMode.trending,
      active: sortMode === SortMode.trending,
      onClick: () => setSortMode(SortMode.trending),
    },
    {
      title: "New",
      id: SortMode.newest,
      active: sortMode === SortMode.newest,
      onClick: () => setSortMode(SortMode.newest),
    },
    {
      title: "Most Viewed",
      id: SortMode.mostViewed,
      active: sortMode === SortMode.mostViewed,
      onClick: () => setSortMode(SortMode.mostViewed),
    },
  ];

  return (
    <Page pageTitle="Discover | Tiny Fiction">
      <Box
        style={{
          paddingTop: 16,
          paddingBottom: 24,
          maxWidth: isDesktop ? 600 : undefined,
        }}
      >
        <Text
          style={{
            fontSize: isDesktop ? 24 : 20,
            fontWeight: 700,
          }}
        >
          Discover
        </Text>
        <Text variant="meta" style={{ marginTop: 4 }}>
          Browse stories from the community
        </Text>
        <TabContainer
          tabs={tabs}
          style={{
            marginTop: 16,
            borderBottom: "1px solid lightgray",
          }}
        />
        <Box style={{ marginTop: 16 }}>
          {stories.map((story, idx) => (
            <DiscoverStoryCard
              key={story.id}
              storyId={story.id}
              storyData={story}
              marginTop={idx > 0}
            />
          ))}
          {loading && (
            <Text
              variant="meta"
              style={{ textAlign: "center", padding: 20 }}
            >
              Loading stories...
            </Text>
          )}
          {!loading && stories.length === 0 && (
            <Flex
              style={{
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 40,
              }}
            >
              <Text
                variant="meta"
                style={{ textAlign: "center" }}
              >
                No stories found yet. Be the first to publish!
              </Text>
            </Flex>
          )}
          {hasMore && !loading && stories.length > 0 && (
            <Flex
              style={{ justifyContent: "center", padding: 16 }}
            >
              <ButtonPill
                variant="outline-secondary"
                size="sm"
                onClick={() => fetchStories(true)}
              >
                Load more
              </ButtonPill>
            </Flex>
          )}
        </Box>
      </Box>
    </Page>
  );
};

export default Discover;
